import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAccessories } from '@/hooks/useCatalog';
import { useDeleteAccessory } from '@/hooks/useCatalogActions';
import type { Accessory } from '@octave/contracts';
import { createLogger } from '@/lib/logger';

const log = createLogger('AccessoriesView');
import { Edit, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { AccessoryForm } from './accessories/AccessoryForm';
import { formatPrice } from '@/lib/format';

export function AccessoriesView() {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingAccessory, setEditingAccessory] = useState<Accessory | null>(
    null
  );

  const { data: accessories, isLoading } = useAccessories();
  const deleteAccessory = useDeleteAccessory();

  const handleEdit = (accessory: Accessory) => {
    setEditingAccessory(accessory);
    setIsFormOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Êtes-vous sûr de vouloir supprimer cet accessoire ?')) {
      try {
        await deleteAccessory.mutateAsync(id);
      } catch (error) {
        log.error('Delete error', error);
      }
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingAccessory(null);
  };

  const formatWeight = (weight: number) => {
    return `${weight}g`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Chargement des accessoires...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Accessoires</h1>
        <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => setEditingAccessory(null)}>
              <Plus className="w-4 h-4 mr-2" />
              Nouvel accessoire
            </Button>
          </DialogTrigger>
          <DialogContent className="!max-w-6xl">
            <DialogHeader>
              <DialogTitle>
                {editingAccessory
                  ? "Modifier l'accessoire"
                  : 'Nouvel accessoire'}
              </DialogTitle>
            </DialogHeader>
            <AccessoryForm
              accessory={editingAccessory}
              onSuccess={handleFormClose}
              onCancel={handleFormClose}
            />
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Liste des accessoires</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Catégorie</TableHead>
                <TableHead>À l'acte</TableHead>
                <TableHead>Poids</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {accessories?.map((accessory) => (
                <TableRow key={accessory.id}>
                  <TableCell className="font-medium">
                    {accessory.name}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="secondary"
                      className="text-sm px-3 py-1 bg-primary/10 text-primary border-primary/20"
                    >
                      {accessory.category}
                    </Badge>{' '}
                  </TableCell>
                  <TableCell className="font-mono">
                    {formatPrice(accessory.price)}
                  </TableCell>
                  <TableCell>{formatWeight(accessory.weight)}</TableCell>
                  <TableCell>
                    <Badge
                      variant={accessory.isActive ? 'default' : 'secondary'}
                      className={`text-sm px-3 py-1 ${
                        accessory.isActive
                          ? 'bg-green-100 text-green-700 border-green-200'
                          : 'bg-gray-100 text-gray-700 border-gray-200'
                      }`}
                    >
                      {accessory.isActive ? 'Actif' : 'Inactif'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(accessory)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(accessory.id)}
                        disabled={deleteAccessory.isPending}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
